<script setup>
import {ref, watch, computed, getCurrentInstance} from 'vue'
import { XIcon } from './Icons.js'

const props = defineProps({
	isOpen: Boolean,
	totalFiltered: Number,
	totalSelected: Number,
})

const emit = defineEmits(['close', 'export'])

const scope = ref('filtered')
const format = ref('csv')

const selectedColumns = ref([
	'date',
	'number',
	'status',
	'recipient',
	'contact',
	'amount',
	'currency',
])

const instance = getCurrentInstance()
const t = (key) => instance?.ctx.$i18n.t(key)

const columns = computed(() => [
	{ id: 'date', label: t('invoices.table_date') },
	{ id: 'number', label: t('invoices.invoice_number') },
	{ id: 'status', label: t('invoices.table_status') },
	{ id: 'recipient', label: t('invoices.table_recipient_name') },
	{ id: 'contact', label: t('invoices.table_recipient_contact') },
	{ id: 'amount', label: t('invoices.table_amount') },
	{ id: 'currency', label: t('invoices.currency') },
])

const toggleColumn = (id) => {
	if (selectedColumns.value.includes(id)) {
		selectedColumns.value = selectedColumns.value.filter(c => c !== id)
	} else {
		selectedColumns.value.push(id)
	}
}

const handleExport = () => {
	emit('export', {
		scope: scope.value,
		format: format.value,
		columns: selectedColumns.value,
	})
}

watch(
	() => props.isOpen,
	(open) => {
		if (open && props.totalSelected > 0) {
			scope.value = 'selected'
		} else {
			scope.value = 'filtered'
		}
	}
)
</script>

<template>
	<div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
		<div class="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 border border-slate-200">

			<!-- Header -->
			<div class="flex justify-between items-center mb-6">
				<h2 class="text-lg font-bold">{{ $t('invoices.export') }}</h2>
				<button @click="emit('close')" class="text-slate-400 hover:text-slate-600">
					<XIcon class="w-5 h-5" />
				</button>
			</div>

			<!-- STEP 1 -->
			<div class="mb-6 space-y-2">
				<p class="text-xs font-bold text-slate-500 uppercase">{{ $t('invoices.step_1_title') }}</p>

				<label
					:class="[
						'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
						scope === 'selected'
							? 'bg-blue-50 border-blue-500'
							: 'bg-white border-slate-200 hover:bg-slate-50',
						totalSelected === 0 && 'opacity-50 cursor-not-allowed'
					]"
				>
					<input
						type="radio"
						name="exportScope"
						class="accent-blue-500 w-4 h-4"
						value="selected"
						v-model="scope"
						:disabled="totalSelected === 0"
					/>

					<span class="text-sm font-medium">
						{{ $t('invoices.export_scope_selected', { count: totalSelected }) }}
					</span>
				</label>

				<label
					:class="[
						'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
						scope === 'filtered'
							? 'bg-blue-50 border-blue-500'
							: 'bg-white border-slate-200 hover:bg-slate-50'
					]"
				>
					<input
						type="radio"
						name="exportScope"
						class="accent-blue-500 w-4 h-4"
						value="filtered"
						v-model="scope"
					/>

					<span class="text-sm font-medium">
						{{ $t('invoices.export_scope_filtered', { count: totalFiltered }) }}
					</span>
				</label>

			</div>

			<!-- STEP 2 -->
			<div class="mb-6 space-y-2">
				<p class="text-xs font-bold text-slate-500 uppercase">{{ $t('invoices.step_2_title') }}</p>

				<div class="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded-lg border">
					<label v-for="c in columns" :key="c.id" class="flex gap-2 items-center">
						<input type="checkbox"
									 :checked="selectedColumns.includes(c.id)"
									 @change="toggleColumn(c.id)" />
						<span class="text-sm">{{ c.label }}</span>
					</label>
				</div>
			</div>

			<!-- STEP 3 -->
			<div class="mb-8 space-y-2">
				<p class="text-xs font-bold text-slate-500 uppercase">{{ $t('invoices.step_3_title') }}</p>

				<label class="flex gap-3 p-3 border rounded-lg cursor-pointer"
							 :class="format === 'csv' ? 'bg-blue-50 border-blue-500' : ''">
					<input type="radio" value="csv" v-model="format" />
					.csv
				</label>

				<label class="flex gap-3 p-3 border rounded-lg cursor-pointer"
							 :class="format === 'excel' ? 'bg-blue-50 border-blue-500' : ''">
					<input type="radio" value="excel" v-model="format" />
					.xls (Excel)
				</label>
			</div>

			<button
				@click="handleExport"
				class="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg"
			>
				{{ $t('invoices.btn_export_download') }}
			</button>

		</div>
	</div>
</template>
